import React, { useEffect, useState } from "react";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import InputField from "../../shared-components/InputField";
import { classNames, uniqueUUID } from "../../utils";
import { PersonFormStateError } from "../PersonFormErrorState";
import { XIcon } from "../../shared-components/icons";
import { NameType } from "../../api/Person";

export interface AliasesDefinitions {
  firstName: string;
  middleName: string;
  lastName: string;
  key: string;
  name_type: NameType;
}

type Props = {
  onChange?: (aliases: AliasesDefinitions[]) => void;
  prefill?: AliasesDefinitions[];
  error?: PersonFormStateError;
  alternateColor?: boolean;
};

function buildEmptyDefinition(isMain: boolean = false): AliasesDefinitions {
  return {
    firstName: "",
    middleName: "",
    lastName: "",
    key: uniqueUUID(),
    name_type: isMain ? NameType.MAIN : NameType.ALIAS,
  };
}

const PersonFormAliases = ({
  onChange,
  error,
  prefill,
  alternateColor,
}: Props) => {
  const [fieldsList, setFields] = useState<AliasesDefinitions[]>([
    buildEmptyDefinition(true),
  ]);
  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (
    idx: number,
    field: "firstName" | "middleName" | "lastName",
    value: string
  ) => {
    fieldsList[idx][field] = value;
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: AliasesDefinitions[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    idx: number
  ) => {
    e.preventDefault();
    removeField(idx);
  };

  useEffect(() => {
    if (prefill && prefill.length > 0) {
      handleChange([...prefill]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  return (
    <div className="flex">
      {fieldsList.length > 0 && (
        <div className="flex-1 mr-4">
          {fieldsList.map((fields, idx) => (
            <div
              key={fields.key}
              className="flex flex-nowrap items-center gap-4 mb-2"
            >
              <InputField
                handleChange={(e) => {
                  updateField(idx, "firstName", e.target.value);
                }}
                name={`first-name-${idx}`}
                placeholder=""
                label={"First Name*"}
                classes="border-rose-600 flex-1"
                value={fields.firstName}
                className={classNames(
                  error?.names?.first_name?.[idx] ? "border-rose-600" : "",
                  "w-full"
                )}
              />

              <InputField
                handleChange={(e) => {
                  updateField(idx, "middleName", e.target.value);
                }}
                name={`middle-name-${idx}`}
                placeholder=""
                label={"Middle Name"}
                value={fields.middleName}
                classes={"w-48"}
              />

              <InputField
                handleChange={(e) => {
                  updateField(idx, "lastName", e.target.value);
                }}
                classes="flex-1"
                name={`last-name-${idx}`}
                placeholder=""
                label={"Last Name *"}
                value={fields.lastName}
                className={classNames(
                  error?.names?.first_name?.[idx] ? "border-rose-600" : "",
                  "w-full"
                )}
              />

              <span className="pl-2 h-0 w-10">
                {idx > 0 && (
                  <div
                    className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                    onClick={(e) => handleRemove(e, idx)}
                  >
                    <XIcon />
                  </div>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="self-end pb-2">
        <Button
          type={ButtonTypes.add}
          text="Add Aliases"
          colorType={
            alternateColor
              ? ButtonColorTypes.alternate
              : ButtonColorTypes.action
          }
          className="w-44"
          onClick={handleAdd}
        />
      </div>
    </div>
  );
};

export default PersonFormAliases;
